// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define.
 *
 * The person here has a scanned photograph of a grandparent, not a graphics
 * degree. The tool nevertheless has to be honest that it is inventing pixels
 * and that its depth has no real-world scale, and those are hard things to say
 * without jargon — so every term the interface uses is defined in place, in a
 * sentence, including the ones that explain what it cannot do.
 */

export const GLOSSARY: Record<string, [string, string]> = {
  depthmap: [
    'Depth map',
    'A greyscale picture the same shape as your photograph, where bright means near and dark means far. It is worked out by a neural network from the picture alone — there was never any distance data in the file.',
  ],
  relative: [
    'No real-world scale',
    'The network reports which things are nearer than which, not how far away anything is. There are no metres in it, and two photographs of the same room can come back with completely different numbers — which is why unflatten measures every picture on its own terms rather than applying a fixed amount of movement.',
  ],
  parallax: [
    'Parallax',
    'The reason the world looks solid when you move your head: near things slide across your view faster than far things. Recreating that sliding from a single photograph is the whole trick here.',
  ],
  disocclusion: [
    'Invented pixels',
    'A photograph only records what the camera could see. Move the viewpoint even slightly and you are asking to see what was hidden behind the foreground — and nothing in the file knows what that was. unflatten stretches the nearest available pixels across the gap, which is a guess. The travel limit is set so the guess stays small enough not to notice, and the hole map shows you exactly where it is.',
  ],
  cap: [
    'The travel limit',
    'How far the camera may move before the invented pixels start to show. It is measured from your own photograph: the tool looks at the biggest depth jump within a small neighbourhood of every pixel, takes the 99.5th percentile of that, and works backwards from how wide a smear is acceptable. A photograph with a hard foreground edge gets a smaller limit than a gentle landscape.',
  ],
  holemap: [
    'Hole map',
    'A view that greys out everything real and paints the invented pixels red. It is measured from what the mesh actually did on screen — the stretch of each triangle — not predicted in advance, so it shows the artefact rather than an estimate of it.',
  ],
  focus: [
    'Focal plane',
    'The distance that stays perfectly still while everything else slides around it, and the distance that stays sharp in the lens-blur still. Put it on your subject and the picture holds together; put it on the background and the subject swims.',
  ],
  coc: [
    'Circle of confusion',
    'A real lens turns a point of light into a disc, and the disc grows the further that point is from the focal plane. Grading the blur by depth, rather than blurring the whole background equally, is the difference between a lens and a smudge.',
  ],
  webgpu: [
    'WebGPU',
    'The browser’s access to your graphics card. Where it is available the depth is worked out in a fraction of a second. Where it is not, the model runs on the processor instead, which a static site like this one can only do on a single core — and that is the entire reason for the wait.',
  ],
  anaglyph: [
    'Red/cyan 3D',
    'The old cardboard-glasses trick: two slightly different views, one tinted for each eye. This one uses the Dubois projection rather than a plain colour split, which keeps skin tones and reds from fighting each other.',
  ],
  sixteenbit: [
    '16-bit',
    'An ordinary greyscale image has 256 levels of grey, which is not enough for depth — you can see the steps as terracing. This one has 65,536, written straight from the network’s own numbers.',
  ],
};

let tip: HTMLElement | null = null;

function hide() {
  tip?.remove();
  tip = null;
}

function show(anchor: HTMLElement, key: string) {
  hide();
  const entry = GLOSSARY[key];
  if (!entry) return;

  const el = document.createElement('div');
  el.id = 'glossary-tip';
  el.setAttribute('role', 'tooltip');
  const strong = document.createElement('strong');
  strong.textContent = entry[0];
  el.append(strong, document.createTextNode(entry[1]));
  document.body.append(el);

  const rect = anchor.getBoundingClientRect();
  const box = el.getBoundingClientRect();
  const margin = 8;
  let left = rect.left;
  if (left + box.width > window.innerWidth - margin) left = window.innerWidth - box.width - margin;
  if (left < margin) left = margin;
  let top = rect.bottom + 6;
  if (top + box.height > window.innerHeight - margin) top = Math.max(margin, rect.top - box.height - 6);
  el.style.left = `${left}px`;
  el.style.top = `${top}px`;
  tip = el;
}

/** Wraps a term for use inside an innerHTML string. */
export function term(key: string, label?: string): string {
  const text = label ?? key;
  if (!GLOSSARY[key]) return text;
  return `<span class="glossary-link" data-term="${key}" role="button" tabindex="0">${text}</span>`;
}

export function initGlossary() {
  document.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('.glossary-link');
    if (!target) {
      if (tip && !tip.contains(event.target as Node)) hide();
      return;
    }
    event.preventDefault();
    const key = target.dataset.term;
    if (key) show(target, key);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && tip) {
      // Only swallow the key when a tooltip is actually up, so Escape still
      // reaches whatever overlay is behind it.
      event.stopPropagation();
      hide();
      return;
    }
    const target = event.target as HTMLElement | null;
    if ((event.key === 'Enter' || event.key === ' ') && target?.classList.contains('glossary-link')) {
      event.preventDefault();
      const key = target.dataset.term;
      if (key) show(target, key);
    }
  });

  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('resize', hide);
}
